import React, { useState, useCallback } from 'react';
import { Input, NNSelect, Label, Button, ICON_PX } from '../ui';
import { Check, X } from 'lucide-react';

const OPERATOR_OPTIONS = [
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: 'at least' },
  { value: '<=', label: 'at most' },
  { value: '==', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: 'truthy', label: 'is set' },
  { value: 'falsy', label: 'is not set' },
  { value: 'includes', label: 'includes' },
];

const COMMON_FIELDS = [
  { value: 'findings.length', label: 'Number of findings' },
  { value: 'patientCount', label: 'Patient count' },
  { value: 'score', label: 'Score' },
  { value: 'status', label: 'Status' },
  { value: 'isUrgent', label: 'Is urgent' },
];

/**
 * Inline condition editor for edge guards.
 * Produces a guard config: { type: 'context', path, operator, value }
 */
function ConditionEditor({ condition, onChange, onRemove }) {
  const [path, setPath] = useState(condition?.path || '');
  const [operator, setOperator] = useState(condition?.operator || '>');
  const [value, setValue] = useState(condition?.value ?? '');
  const [customField, setCustomField] = useState(!COMMON_FIELDS.some((f) => f.value === (condition?.path || '')));

  const noValueNeeded = operator === 'truthy' || operator === 'falsy';

  const handleSave = useCallback(() => {
    if (!path) return;
    onChange?.({
      type: 'context',
      path,
      operator,
      ...(noValueNeeded ? {} : { value: isNaN(value) ? value : Number(value) }),
    });
  }, [path, operator, value, noValueNeeded, onChange]);

  const handleFieldSelect = useCallback((val) => {
    if (val === '__custom') {
      setCustomField(true);
      setPath('');
    } else {
      setCustomField(false);
      setPath(val);
    }
  }, []);

  return (
    <div className="space-y-2 p-2 rounded-lg border border-slate-200 bg-slate-50">
      <Label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Condition</Label>

      {/* Field selector */}
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">When</Label>
        {customField ? (
          <Input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="context.field.path"
            className="text-xs"
          />
        ) : (
          <NNSelect
            value={path}
            onChange={handleFieldSelect}
            options={[
              ...COMMON_FIELDS,
              { value: '__custom', label: 'Custom field...' },
            ]}
            placeholder="Choose a field"
          />
        )}
      </div>

      {/* Operator */}
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Is</Label>
        <NNSelect
          value={operator}
          onChange={setOperator}
          options={OPERATOR_OPTIONS}
        />
      </div>

      {/* Value (hidden for truthy/falsy) */}
      {!noValueNeeded && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Value</Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g., 0"
            className="text-xs"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1.5 pt-1">
        <Button variant="save" size="sm" onClick={handleSave} disabled={!path} className="flex-1">
          <Check size={ICON_PX.sm} className="mr-1" /> Save
        </Button>
        {onRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <X size={ICON_PX.sm} />
          </Button>
        )}
      </div>

      {/* Preview */}
      {path && (
        <div className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-200">
          if {path} {operator} {noValueNeeded ? '' : value}
        </div>
      )}
    </div>
  );
}

/**
 * Summarize a condition config into a human-readable string.
 */
export function summarizeCondition(condition) {
  if (!condition) return '';
  if (typeof condition === 'string') return condition;
  const { path, operator, value } = condition;
  const opLabels = { '>': '>', '<': '<', '>=': '>=', '<=': '<=', '==': '=', '!=': '!=', truthy: 'is set', falsy: 'is not set', includes: 'includes' };
  const op = opLabels[operator] || operator;
  if (operator === 'truthy' || operator === 'falsy') {
    return `${path} ${op}`;
  }
  return `${path} ${op} ${value}`;
}

export default ConditionEditor;
